/* A welcome for brand-new people (owner request 2026-10-15). Someone who records time and has no entry at all and no
   running timer sees, once on each device: a card ("Yay, you're here now! 🎉") with falling confetti in autumn colours,
   then, if they tap "Show me how", a three-step guide that points at the real Timer controls. The first entry they save
   afterwards on this device gets "First one done! ✅" (once).
   "No entry at all" is asked of the server (one row, the person's own; RLS applies), only after the data has loaded and
   only when this device holds nothing of theirs, so older entries outside the device's window count too.
   The card and the guide are help, so they always show; only the confetti follows Settings → Little surprises, and
   "reduce motion" means no confetti. Nothing here changes, creates or deletes a record. */
import {State, ui} from './state.ts';
import {can} from './lib/model.ts';
import type {Op} from './lib/sync.ts';
import * as db from './db.ts';
import * as sync from './syncer.ts';
import {toast} from './ui/common.ts';
import {surprisesOn} from './surprises.ts';
import {welcomeDue, guideSteps, placeTip, union, type Box, type Step} from './lib/welcomeLogic.ts';

const DONE = (id: string) => 'ot_welcomed_' + id;      // welcomed (or found to have entries) on this device
const FIRST = (id: string) => 'ot_first_entry_' + id;  // welcomed here: cheer their first saved entry
const get = (k: string) => { try { return localStorage.getItem(k); } catch { return null; } };
const put = (k: string, v: string | null) => { try { v === null ? localStorage.removeItem(k) : localStorage.setItem(k, v); } catch { /* private mode */ } };
const reduced = () => { try { return matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; } };
const $id = (id: string) => document.getElementById(id);

/** what this device holds of the person: anything at all means "not brand-new" */
function local(meId: string) {
  return {canTrack: can(State.role, 'trackTime'), welcomedHere: !!get(DONE(meId)),
    ownEntries: State.entries.filter(e => e.memberId === meId).length, ownTimer: State.timers.some(t => t.memberId === meId),
    pending: sync.pendingCount(), notSaved: sync.notSavedItems().length};
}

let checking = false;
/** after the data has loaded (main.ts): show the welcome if this person is brand-new */
export async function maybeWelcome(): Promise<void> {
  const meId = State.meId;
  if (!meId || checking || !navigator.onLine) return;
  const l = local(meId);
  if (l.ownEntries || l.ownTimer) { if (l.canTrack && !l.welcomedHere) put(DONE(meId), 'has'); return; }
  if (!welcomeDue(l)) return;
  checking = true;
  try {
    const has = await db.hasOwnEntry(meId);
    if (State.meId !== meId) return;                          // signed out meanwhile
    if (has) { put(DONE(meId), 'has'); return; }               // entries older than this device keeps: never welcomed
    if (!welcomeDue(local(meId))) return;                     // started a timer or saved something meanwhile
    put(DONE(meId), new Date().toISOString().slice(0, 10)); put(FIRST(meId), '1');
    openWelcome();
  } catch { /* offline or the server did not answer: ask again at the next start */ }
  finally { checking = false; }
}

/* ---------- the card ---------- */
function closeWelcome() { $id('wlCard')?.remove(); }
function openWelcome() {
  closeWelcome();
  const bg = document.createElement('div'); bg.id = 'wlCard'; bg.className = 'sx-card-bg wl-bg';
  bg.innerHTML = `<div class="sx-card wl-card" role="dialog" aria-modal="true" aria-labelledby="wlTitle">
    <h2 id="wlTitle" class="wl-title">Yay, you're here now! 🎉</h2>
    <p class="wl-sub">Let's get the party started</p>
    <button type="button" class="btn primary wl-go" id="wlShow">Show me how</button>
    <button type="button" class="wl-skip" id="wlSkip">Skip</button></div>`;
  document.body.appendChild(bg);
  $id('wlShow')!.onclick = () => { closeWelcome(); startGuide(); };
  $id('wlSkip')!.onclick = closeWelcome;
  ($id('wlShow') as HTMLButtonElement).focus({preventScroll: true});
  if (surprisesOn() && !reduced()) confetti();
}
const AUTUMN = ['#f39a3d', '#d9701a', '#c4620f', '#f5c84c', '#d6b33e', '#d43a3a', '#b5302a', '#8a5a33', '#6b4325', '#f6e7cf'];   // oranges, golds, reds, browns, cream
function confetti(n = 46) {
  const box = document.createElement('div'); box.className = 'sx-fx sx-confetti wl-confetti'; box.setAttribute('aria-hidden', 'true');
  box.innerHTML = Array.from({length: n}, (_, i) => {
    const c = AUTUMN[i % AUTUMN.length], dur = 2.2 + Math.random() * 1.2, delay = Math.random() * 1.1;
    return `<i style="left:${(Math.random() * 100).toFixed(1)}%; --dur:${dur.toFixed(2)}s; --d:${delay.toFixed(2)}s; --x:${(Math.random() * 90 - 45).toFixed(0)}px; --r:${(Math.random() * 720 - 360).toFixed(0)}deg; --c:${c};"></i>`;
  }).join('');
  document.body.appendChild(box);
  setTimeout(() => box.remove(), 4600);
}

/* ---------- the guide: a bubble with an arrow at the real control, the control lit up ---------- */
let steps: Step[] = [], at = 0, follow: ReturnType<typeof setInterval> | undefined;
function startGuide() {
  const meId = State.meId; if (!meId) return;
  const noProjects = !(State.role === 'owner' || State.role === 'admin') && !(State.assign || []).some(a => a.memberId === meId);
  steps = guideSteps(noProjects); at = 0;
  if (State.tab !== 'timer') { State.tab = 'timer'; ui.render(); }
  const g = document.createElement('div'); g.id = 'wlGuide'; g.className = 'wl-guide';
  g.innerHTML = `<div class="wl-ring" id="wlRing"></div>
    <div class="wl-tip" id="wlTip" role="dialog" aria-live="polite"><i class="wl-arrow" id="wlArrow"></i>
      <p class="wl-count" id="wlCount"></p><p class="wl-text" id="wlText"></p>
      <div class="wl-acts"><button type="button" class="wl-skip" id="wlTipSkip">Skip</button><button type="button" class="btn primary" id="wlNext">Next</button></div></div>`;
  document.body.appendChild(g);
  $id('wlNext')!.onclick = () => { if (at >= steps.length - 1) endGuide(); else { at++; showStep(true); } };
  $id('wlTipSkip')!.onclick = endGuide;
  window.addEventListener('resize', place); window.addEventListener('scroll', place, {passive: true});
  follow = setInterval(place, 400);   // the screen redraws now and then (live updates): keep pointing at the new element
  requestAnimationFrame(() => requestAnimationFrame(() => showStep(true)));   // after the Timer screen is drawn
}
function endGuide() {
  clearInterval(follow); follow = undefined;
  window.removeEventListener('resize', place); window.removeEventListener('scroll', place);
  $id('wlGuide')?.remove();
}
const found = (s: Step) => s.target.map(id => $id(id)).filter((e): e is HTMLElement => !!e && e.getClientRects().length > 0);
function showStep(scroll: boolean) {
  const s = steps[at]; if (!s || !$id('wlGuide')) return;
  // (V1 names its button "+ Add manual entry": the bubble says what is really on screen)
  const byHand = s.last && $id('openManualEntryBtn')?.textContent?.trim();
  $id('wlText')!.textContent = byHand ? s.text.replace('+ Add time by hand', byHand) : s.text;
  $id('wlCount')!.textContent = steps.length > 1 ? `${at + 1} of ${steps.length}` : '';
  $id('wlNext')!.textContent = s.last ? 'Got it' : 'Next';
  ($id('wlTipSkip') as HTMLElement).hidden = s.last;
  $id('wlGuide')!.dataset.step = String(at + 1);
  const els = found(s);
  if (scroll && els[0]) els[0].scrollIntoView({block: 'center', inline: 'nearest'});
  place();
}
const boxOf = (e: Element): Box => { const r = e.getBoundingClientRect(); return {left: r.left, top: r.top, width: r.width, height: r.height}; };
function place() {
  const s = steps[at], tip = $id('wlTip'), ring = $id('wlRing'), arrow = $id('wlArrow');
  if (!s || !tip || !ring || !arrow) return;
  const els = found(s), target = els[0] || null;
  // light up the pointed-at element (step 1: the description box with its client and project boxes, when they sit together)
  const lit = s.target[0] === 'timerDesc' ? union(els.map(boxOf)) : target ? boxOf(target) : null;
  const litOk = lit && lit.height < innerHeight * 0.6 ? lit : target ? boxOf(target) : null;
  const vw = document.documentElement.clientWidth, vh = innerHeight;
  const head = document.querySelector('header.topbar')?.getBoundingClientRect(), nav = $id('tabs')?.getBoundingClientRect();
  const topBar = head ? Math.max(0, head.bottom) : 0, bottomBar = nav && nav.height ? Math.max(0, vh - nav.top) : 0;
  const w = Math.min(320, vw - 20), h = tip.offsetHeight || 140;
  tip.style.width = w + 'px';
  const p = placeTip(litOk, target ? boxOf(target) : null, w, tip.offsetHeight || h, vw, vh, topBar, bottomBar);
  tip.style.left = p.left + 'px'; tip.style.top = p.top + 'px'; tip.dataset.side = p.side;
  arrow.hidden = p.side === 'center'; arrow.style.left = p.arrowX + 'px';
  if (litOk && p.side !== 'center') {
    const pad = 6; ring.hidden = false;
    Object.assign(ring.style, {left: litOk.left - pad + 'px', top: litOk.top - pad + 'px', width: litOk.width + pad * 2 + 'px', height: litOk.height + pad * 2 + 'px'});
  } else ring.hidden = true;
  $id('wlGuide')!.classList.toggle('dim', ring.hidden);
}

/* ---------- the first entry after the welcome (syncer: the server has confirmed it) ---------- */
export function onSaved(op: Op) {
  const meId = State.meId; if (!meId || !get(FIRST(meId))) return;
  const e = op.kind === 'stopTimer' ? op.entry : op.kind === 'saveEntry' && op.isNew ? op.entry : null;
  if (!e || e.memberId !== meId) return;
  put(FIRST(meId), null);
  if (State.entries.some(x => x.memberId === meId && x.id !== e.id)) return;   // entries came from another device first: not their first
  setTimeout(() => toast('First one done! ✅', 4000), 1800);   // after the "Timer stopped" / "Entry saved" message
}
/** sign-out: nothing of the welcome stays on screen */
export function closeWelcomeAll() { closeWelcome(); endGuide(); }
